import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { Appointment } from '@adornis/buchungsheld-db/entities/appointment.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Role } from '@adornis/buchungsheld-db/enums/roles.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { changePassword } from '@adornis/users/db/a-user.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filter, of, switchMap } from 'rxjs';

// TODO check redundancy with bh-prompt-user-picker

@customElement('bh-prompt-admin-modify')
export class BHPromptAdminModify extends TrDialog<void> {
  static override get element_name() {
    return 'bh-prompt-admin-modify';
  }

  @property({ attribute: false }) userID = new RXController<Maybe<string>>(this, undefined);
  @property({ attribute: false }) user = new RXController(
    this,
    this.userID.observable.pipe(
      filter(Boolean),
      switchMap(userID => {
        if (userID === 'new') return of(new User({}));
        return getByID(
          User,
          (console.log({ userID }), userID),
        )({
          _id: 1,
          email: 1,
          firstName: 1,
          lastName: 1,
          username: 1,
          participations: { _id: 1 },
          roles: { ...AdornisRoleToContextsWrapper.allFields, name: 1, contexts: 1 },
        });
      }),
    ),
  );
  protected readonly _users = new RXController(
    this,
    User.subscribeAllNonAdmins()({
      _id: 1,
      email: 1,
      firstName: 1,
      lastName: 1,
      username: 1,
      participations: { _id: 1 },
      roles: { ...AdornisRoleToContextsWrapper.allFields, name: 1, contexts: 1 },
    }),
  );

  protected _form = new FormController(this, this.user.observable.pipe(filter(Boolean)), {
    validationMeta: () => ({
      validateOmitted: false,
    }),
    success: async instance => {
      if (this.newPassword !== '') {
        try {
          // @ts-expect-error not sure why this does throws error
          await changePassword(instance._id, this.newPassword, User);
        } catch (e: any) {
          await XSnackbar.show(e.message);
          return;
        }
      }

      if (!instance.username) instance.username = instance.email;
      if (this._isAddMode && !instance._id) {
        const id = await instance.create();
        this.userID.next(id);
        this._isAddMode = false;
        return;
      }

      instance.participations = [];
      await XDialog.waitFor(
        (async () => {
          await instance.save();
          const isGlobalAdmin = !!instance.roles.find(role => role.name === Role.SUPER_ADMIN);
          const adminContexts = this.selectedInstitutions?.map(i => i._id) ?? [];
          console.log('update roles', { id: instance._id, isGlobalAdmin, adminContexts });
          await User.updateUserRoles(instance._id, isGlobalAdmin, adminContexts)();
        })(),
      );
      return this.close();
    },
  });
  @state() private readonly _institutions = new RXController(
    this,
    Institution.getAll<Institution>()(Institution.allFields),
  );

  private selectedInstitutions: Institution[] | null = null;
  private newPassword = '';
  @state() protected _isAddMode = false;

  override render() {
    if (
      this._users.isLoading ||
      !this._users.value ||
      this._institutions.isLoading ||
      (!!this.userID.value && !this.user.value) // if userID is set but user is not loaded yet, show loading
    )
      return html` <x-loading-state no-wrapper></x-loading-state> `;

    return html`
      <x-flex space="md"> ${this._isAddMode ? this.addNewUser() : this.chooseUser()} ${this.buttons()} </x-flex>
    `;
  }

  protected addNewUser() {
    return html`
      <x-text> Sie wollen doch keinen Nutzer anlegen? </x-text>
      <tr-button-small
        icon="list"
        .action=${() => {
          if (!this._form.document) return;
          this.userID.next(undefined);
          this._isAddMode = false;
        }}
      >
        <x-text> Nutzer auswählen </x-text>
      </tr-button-small>
      <x-flex space="sm">
        <x-input
          placeholder="E-Mail"
          ${this._form.field('email')}
          @value-picked=${(e: ValueEvent<string>) =>
            !!this._form.document && (this._form.document.username = e.detail.value)}
        ></x-input>
        <x-input placeholder="Vorname" ${this._form.field('firstName')}></x-input>
        <x-input placeholder="Nachname" ${this._form.field('lastName')}></x-input>
      </x-flex>
    `;
  }

  protected chooseUser() {
    const contexts = this.user.value?.roles.find(role => role.name === Role.ADMIN)?.contexts;
    const selecetedInstitutions = contexts ? this._institutions.value?.filter(i => contexts.includes(i._id)) : [];

    if (!this.user.value)
      return html`
        <x-flex space="lg">
          ${!this.user.value
            ? html` <x-h4> ${this._translation.translate('PROMPT', 'user_management')} </x-h4> `
            : html` <x-h4> ${this._translation.translate('PROMPT', 'edit_admins')} </x-h4> `}
          <tr-button-small
            icon="add"
            .action=${() => {
              this.userID.next('new');
              this._isAddMode = true;
            }}
          >
            <x-text>${this._translation.translate('create')}</x-text>
          </tr-button-small>
          <tr-dropdown-search-selection
            empty-state-with-search=${this._translation.translate('dropdowns', 'emptyStateUsers')}
            empty-state-without-search=${this._translation.translate('dropdowns', 'emptyStateUsersWithoutSearch')}
            placeholder="Nutzer auswählen"
            @value-picked=${(e: ValueEvent<User>) => {
              this.userID.next(e.detail.value._id);
            }}
            .value=${this.user.value}
            .search=${async search => {
              const users = this._users.value;
              if (!users || !search) return [];
              const emailRegexp = new RegExp(search.replace(/([^\w]{1})/g, '\\$1'), 'i');
              return users.filter(
                user =>
                  emailRegexp.test(user.email) ||
                  emailRegexp.test(user.firstName) ||
                  emailRegexp.test(user.lastName) ||
                  emailRegexp.test(`${user.firstName} ${user.lastName}`) ||
                  emailRegexp.test(`${user.lastName} ${user.firstName}`),
              );
            }}
            .renderString=${doc => doc?.email ?? ''}
            clearable
          ></tr-dropdown-search-selection>
        </x-flex>
      `;

    return html`
      <x-flex space="md">
        <x-h4> ${this._translation.translate('PROMPT', 'edit_user_rights')} </x-h4>
        <x-checkbox
          .label=${'Globaler Admin'}
          .value=${!!this.user.value.roles.find(role => role.name === Role.SUPER_ADMIN)}
          @value-picked=${(e: ValueEvent<boolean>) => {
            const setGlobalAdmin = e.detail.value;
            const admin = this.user.value;
            if (!admin) return;
            if (setGlobalAdmin) {
              admin.roles.push(
                new AdornisRoleToContextsWrapper({ name: Role.SUPER_ADMIN, contexts: [GLOBAL_CONTEXT] }),
              );
            } else {
              const index = admin.roles.findIndex(role => role.name === Role.SUPER_ADMIN);
              if (index === -1) return;
              admin.roles.splice(index, 1);
            }
            this.requestUpdate();
          }}
        ></x-checkbox>
        ${this.user.value.roles.find(role => role.name === Role.SUPER_ADMIN)
          ? nothing
          : html`
              <x-dropdown-multiselection
                .value=${selecetedInstitutions}
                placeholder="Auf welche Institutionen darf dieser Admin Zugriff bekommen?"
                @value-picked=${(e: ValueEvent<Institution[]>) => (this.selectedInstitutions = e.detail.value)}
                .selectables=${this._institutions.value}
                .renderString=${i => i.name}
              ></x-dropdown-multiselection>
              <tr-input
                placeholder="Passwort"
                type="text"
                @value-picked=${e => (this.newPassword = e.detail.value)}
              ></tr-input>
            `}
      </x-flex>
    `;
  }

  protected buttons() {
    return html`
      <x-flex horizontal space-between crossaxis-center wrap space="md">
        <x-button
          mode="outline"
          @click=${() => {
            return this.cancel();
          }}
        >
          ${this._translation.translate('discard')}
        </x-button>
        <x-button
          @click=${() => {
            if (!this._form.document) return;

            this._form.document.participations = [];
            return this._form.validate();
          }}
          ${css({ textTransform: 'capitalize' })}
        >
          ${this._translation.translate(this._isAddMode ? 'create' : 'save')}
        </x-button>
      </x-flex>
    `;
  }

  static add() {
    return this.showPopup<Maybe<Appointment>>({ props: {} });
  }

  static edit(user: User) {
    return this.showPopup<Maybe<Appointment>>({ props: { userID: user._id } });
  }
}
